import { useEffect, useRef } from 'react';

/**
 * Take the focus and select the text of a field, once PER REQUEST, when a
 * gesture asks for it (refonte modulaire §4.2; issue #117).
 *
 * « La copie est sélectionnée après le geste, son champ de nom avec elle quand
 * elle en a un » - which the step editor and the objective list both owe, on
 * two different shapes of state. Written twice it was written twice wrong, so
 * it is written here once.
 *
 * ## `token` IDENTIFIES the request, and a boolean cannot
 *
 * `token` is the id of the copy whose field should be grabbed, or `null` for
 * « nothing to do ». A boolean was the first spelling and it was wrong in a way
 * only a second duplication shows: `StepEditor` is ONE long-lived instance -
 * the page selects another step, it does not remount - so a `done` flag set by
 * the first copy is still set when the second arrives, and that copy's name is
 * never focused. Keyed on the id, each copy is its own request and the guard
 * answers correctly for all of them.
 *
 * ## Why once per request
 *
 * The field must take the focus when the copy appears and never again FOR THAT
 * COPY: the very keystroke the operator then types causes a render, and a
 * second `select()` would highlight the whole name under their fingers so the
 * next character wipes it.
 *
 * ## Two guards, because a ref alone dies with the component
 *
 * A row keyed by its id UNMOUNTS when another step is selected and REMOUNTS
 * with a fresh ref when the operator comes back - and if the caller still says
 * « focus this one », the field would be grabbed again, minutes later, on a
 * screen the operator was only reading. So the caller is also handed `onDone`
 * and is expected to forget: the hook fires, then says so, and a request that
 * has been cleared cannot come back.
 *
 * ## The focus is claimed only if there is something to focus
 *
 * The guard is set AFTER the node has been focused, not before. A render where
 * the ref is still empty - the draft and the selection landing in two passes, a
 * transition, a suspended boundary - would otherwise burn the request and the
 * copy would never be selected at all.
 */
export function useFocusOnce(
  token: string | null,
  ref: { current: HTMLInputElement | null },
  onDone: () => void,
): void {
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (token === null || done.current === token) return;
    const node = ref.current;
    if (node === null) return;
    done.current = token;
    node.focus();
    node.select();
    onDone();
  }, [token, ref, onDone]);
}
